import {
  LineChartOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Flex, Switch } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { MOBILE_MAX_PARAMETERS } from 'business/data-analysis/constants';
import { GenerateGraphsButton } from 'business/data-analysis/pages/graph/components/generate-graphs-button';
import styles from 'business/data-analysis/pages/graph/components/graph-card/index.module.scss';
import GraphSection from 'business/data-analysis/pages/graph/components/graph-section';
import { ParameterCountTag } from 'business/data-analysis/pages/graph/components/parameter-count-tag';
import ParameterList from 'business/data-analysis/pages/graph/components/parameter-list';
import { ParameterSidebar } from 'business/data-analysis/pages/graph/components/parameter-sidebar';
import {
  useGraphSet,
  useGraphSetDispatch,
} from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { GraphSetDispatchActions } from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useGraph } from 'business/data-analysis/pages/graph/hooks/use-graph';
import { GraphFilters } from 'business/data-analysis/pages/graph/types';

type SingleGraphContainerProps = {
  filters: GraphFilters;
};

export const SingleGraphContainer = ({
  filters,
}: SingleGraphContainerProps) => {
  const { t } = useTranslation();
  const { structure } = useGraphSet();

  const graphId = Array.from(structure.graphs.keys())[0];
  const graph = useGraph(graphId);
  const dispatch = useGraphSetDispatch();
  const [showGraph, setShowGraph] = useState<boolean>(true);

  const openParameterSidebar = () => {
    dispatch({ type: GraphSetDispatchActions.SetCurrentGraphId, graphId });
  };

  const atLeastOneParameterIsSelected = graph.parameters.size > 0;

  const initialZoom =
    filters.dateFilters.endDate.diff(filters.dateFilters.startDate, 'hour') > 1;

  return (
    <Flex vertical gap={22}>
      <Flex align="center" justify="space-between">
        <Switch
          value={showGraph}
          onChange={(value) => {
            setShowGraph(value);
          }}
          checkedChildren={<LineChartOutlined />}
          unCheckedChildren={<SettingOutlined />}
          defaultChecked
        />
        <GenerateGraphsButton
          atLeastOneParameterIsSelected={atLeastOneParameterIsSelected}
          filters={filters}
        />
      </Flex>

      {showGraph ? (
        <GraphSection graphId={graphId} initialZoom={initialZoom} />
      ) : (
        <>
          <ParameterCountTag
            displayTitle
            maxParameters={MOBILE_MAX_PARAMETERS}
            selectedParameterCount={graph.parameters.size}
          />
          <ParameterList graphId={graphId} />
        </>
      )}

      <Button
        className={styles.addParameter}
        type="primary"
        onClick={openParameterSidebar}
        icon={<PlusOutlined />}
      >
        {t('dataAnalysis.actions.addParameter')}
      </Button>
      <ParameterSidebar placement="bottom" />
    </Flex>
  );
};
